// Add this import at the top
import './MyProfile.css';
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { authService } from '../services/api';
import axios from 'axios';
import { config } from '../services/api';

function MyProfile() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  // Update the state to match the backend fields
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    address: '',
    avatar: null
  });
  const [avatarPreview, setAvatarPreview] = useState(null);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        if (!token) {
          setError('Vous devez être connecté pour accéder à cette page');
          setLoading(false);
          return;
        }
        
        const response = await axios.get(`${config.API_URL}/api/users/profile/`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        setProfileData(response.data);
        if (response.data.avatar) {
          console.log("AVATAR    ",response.data.avatar);
          setAvatarPreview(response.data.avatar);
        }
      } catch (err) {
        console.error('Error fetching profile:', err);
        setError('Impossible de charger votre profil. Veuillez réessayer plus tard.');
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileData(prev => ({
        ...prev,
        avatar: file
      }));
      
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setSaving(true);
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      
      // Handle each field separately to ensure proper formatting
      if (profileData.first_name) formData.append('first_name', profileData.first_name);
      if (profileData.last_name) formData.append('last_name', profileData.last_name);
      if (profileData.username) formData.append('username', profileData.username);
      if (profileData.phone) formData.append('phone', profileData.phone);
      if (profileData.address) formData.append('address', profileData.address);
      
      // Only append avatar if it's a File object (not a string URL)
      if (profileData.avatar && profileData.avatar instanceof File) {
        formData.append('avatar', profileData.avatar);
      }
      
      // Log the form data for debugging
      for (let pair of formData.entries()) {
        console.log(pair[0] + ': ' + pair[1]);
      }
      
      const response = await axios.patch(`${config.API_URL}/api/users/profile/`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      console.log('Profile update response:', response.data);
      setSuccess('Votre profil a été mis à jour avec succès');
      
      // Update user name in localStorage for the header component
      localStorage.setItem('user', profileData.username);
      
      // Trigger auth change event to update header
      window.dispatchEvent(new Event('authChange'));
    } catch (err) {
      console.error('Error updating profile:', err);
      // Show more detailed error message if available
      if (err.response && err.response.data) {
        console.log('Error details:', err.response.data);
        // If the error response contains field-specific errors
        if (typeof err.response.data === 'object') {
          const errorMessages = Object.entries(err.response.data)
            .map(([field, errors]) => `${field}: ${errors.join(', ')}`)
            .join('; ');
          setError(`Erreur: ${errorMessages}`);
        } else {
          setError(`Une erreur est survenue: ${err.response.data}`);
        }
      } else {
        setError('Une erreur est survenue lors de la mise à jour de votre profil');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container className="py-5 mt-5">
        <div className="text-center my-5">
          <Spinner animation="border" variant="danger" />
          <p className="mt-3">Chargement de votre profil...</p>
        </div>
      </Container>
    );
  }

  // In the return statement, update the form to include first_name and last_name fields
  return (
    <Container className="py-5 mt-5">
      <h2 className="mb-4">Mon Profil</h2>
      
      {/* Alert messages */}
      {error && (
        <Alert variant="danger" className="mb-4">
          <i className="fas fa-exclamation-circle me-2"></i>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert variant="success" className="mb-4">
          <i className="fas fa-check-circle me-2"></i>
          {success}
        </Alert>
      )}
      
      <Row>
        <Col md={4} className="mb-4">
          {/* Left column with avatar and basic info */}
          <Card className="text-center p-4">
            {/* Avatar section */}
            <div className="avatar-upload mb-3">
              {avatarPreview ? (
                <img 
                  src={avatarPreview} 
                  alt="Profile" 
                  className="rounded-circle img-thumbnail mx-auto d-block profile-avatar"
                />
              ) : (
                <div className="profile-avatar-placeholder mx-auto">
                  {profileData.username ? profileData.username.charAt(0).toUpperCase() : 'P'}
                </div>
              )}
              <div className="mt-3">
                <Form.Group controlId="avatar">
                  <Form.Label className="btn btn-outline-secondary btn-sm">
                    <i className="fas fa-camera "></i>
                    {/* Changer la photo */}
                  </Form.Label>
                  <Form.Control 
                    type="file" 
                    onChange={handleAvatarChange}
                    accept="image/*"
                    className="d-none"
                  />
                </Form.Group>
              </div>
            </div>
            
            <h4 className="mb-1">{profileData.username}</h4>
            <p className="text-muted">{profileData.email}</p>
            
            <div className="mt-3">
              <p className="mb-1">
                <i className="fas fa-user me-2"></i>
                {authService.getUserRole() === 'visitor' ? 'Utilisateur' : 
                 authService.getUserRole() === 'partner_admin' ? 'Administrateur Partenaire' :
                 authService.getUserRole() === 'delta_admin' ? 'Administrateur Delta' : 
                 'Super Administrateur'}
              </p>
              <p className="mb-0">
                <i className="fas fa-calendar-check me-2"></i>
                Membre depuis {new Date(profileData.date_joined).toLocaleDateString()}
              </p>
              
              {/* Add Password Change Button */}
              <Button 
                variant="outline-secondary" 
                className="mt-3 w-100"
                onClick={() => window.location.href = '/reset-password'}
              >
                <i className="fas fa-key me-2"></i>
                Modifier Mot de Passe
              </Button>
            </div>
          </Card>
        </Col>
        
        <Col md={8}>
          <Card className="p-4">
            <h3 className="mb-4">Informations personnelles</h3>
            
            <Form onSubmit={handleSubmit}>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="first_name">
                    <Form.Label>Prénom</Form.Label>
                    <Form.Control
                      type="text"
                      name="first_name"
                      value={profileData.first_name || ''}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Col>
                
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="last_name">
                    <Form.Label>Nom</Form.Label>
                    <Form.Control
                      type="text"
                      name="last_name"
                      value={profileData.last_name || ''}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="username">
                    <Form.Label>Nom d'utilisateur</Form.Label>
                    <Form.Control
                      type="text"
                      name="username"
                      value={profileData.username || ''}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Col>
                
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="email">
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={profileData.email || ''}
                      onChange={handleChange}
                      required
                      disabled
                    />
                    <Form.Text className="text-muted">
                      L'email ne peut pas être modifié
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="phone">
                    <Form.Label>Téléphone</Form.Label>
                    <Form.Control
                      type="tel"
                      name="phone"
                      value={profileData.phone || ''}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="address">
                    <Form.Label>Adresse</Form.Label>
                    <Form.Control
                      type="text"
                      name="address"
                      value={profileData.address || ''}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <div className="d-flex justify-content-end mt-3">
                <Button 
                  variant="danger" 
                  type="submit"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Enregistrement...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save me-2"></i>
                      Enregistrer les modifications
                    </>
                  )}
                </Button>
              </div>
            </Form>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default MyProfile;